/**
 * WorkspaceList — card grid for community problem workspaces.
 *
 * Mirrors the ProjectCard visual pattern:
 *   - Full-bleed hero image (or gradient placeholder with warning icon)
 *   - Bottom scrim: presenting concern + phase/category meta
 *   - Hover overlay: full concern text + team/evidence/solution stats
 *   - Red accent strip (left border) — problems domain color
 *   - Camera icon on hover → file picker → crop overlay → MinIO upload
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchWorkspaces,
  updateWorkspace,
  deleteWorkspace,
  WorkspaceResponse,
  WORKSPACE_PHASES,
} from "../../api/workspaces";
import { uploadDataUrl, fileUrl } from "../../api/fileStorage";
import WorkspaceCard, { LocationBadge } from "../WorkspaceCard/WorkspaceCard";
import styles from "./WorkspaceList.module.css";

/* ── Constants ── */
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const JPEG_QUALITY = 0.92;
const CROP_OUTPUT_W = 960;

/* ── Types ── */
interface CropState {
  src: string;
  naturalW: number;
  naturalH: number;
  workspaceGuid: string;
}

/* ── Props ── */
interface Props {
  communityGuid: string;
  onOpen: (workspaceGuid: string) => void;
  onCreate: () => void;
  refreshKey?: number;
  /** Max cards to show before "View all" link. 0 = unlimited. */
  maxVisible?: number;
  /** Callback when "View all" is clicked. */
  onViewAll?: () => void;
  /** Override section title (e.g. "Priority Problems"). */
  sectionTitle?: string;
  /** Community name for the location badge on each card. */
  communityName?: string;
  /** Country flag emoji for the location badge. */
  communityFlag?: string;
}

/* ================================================================== */
/*  CardCropOverlay — crop modal sized to card aspect ratio (~13:14)   */
/* ================================================================== */

function CardCropOverlay({
  crop,
  onConfirm,
  onCancel,
}: {
  crop: CropState;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
}) {
  const { t } = useAppTranslation("pages");
  const viewportRef = useRef<HTMLDivElement>(null);
  const [vpW, setVpW] = useState(360);
  const [vpH, setVpH] = useState(390);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number; startY: number;
    panX: number; panY: number;
  } | null>(null);

  const TARGET_ASPECT = 260 / 280;

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setVpW(e.contentRect.width);
        setVpH(e.contentRect.height);
      }
    });
    ro.observe(el);
    setVpW(el.clientWidth || 360);
    setVpH(el.clientHeight || 390);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const { naturalW, naturalH } = crop;
  const baseScale = Math.max(vpW / naturalW, vpH / naturalH);
  const displayScale = baseScale * zoom;
  const displayW = naturalW * displayScale;
  const displayH = naturalH * displayScale;

  const clampPan = useCallback(
    (px: number, py: number, z?: number) => {
      const ds = baseScale * (z ?? zoom);
      const dw = naturalW * ds;
      const dh = naturalH * ds;
      const mx = Math.max(0, (dw - vpW) / 2);
      const my = Math.max(0, (dh - vpH) / 2);
      return {
        x: Math.max(-mx, Math.min(mx, px)),
        y: Math.max(-my, Math.min(my, py)),
      };
    },
    [baseScale, naturalW, naturalH, vpW, vpH, zoom]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX, startY: e.clientY,
        panX: pan.x, panY: pan.y,
      };
    },
    [pan]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPan(clampPan(dragRef.current.panX + dx, dragRef.current.panY + dy));
    },
    [clampPan]
  );

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const next = Math.max(1, Math.min(4, zoom - e.deltaY * 0.002));
      setZoom(next);
      setPan((p) => clampPan(p.x, p.y, next));
    },
    [zoom, clampPan]
  );

  const handleZoomSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = parseFloat(e.target.value);
      setZoom(next);
      setPan((p) => clampPan(p.x, p.y, next));
    },
    [clampPan]
  );

  const handleConfirm = useCallback(() => {
    const visSrcW = vpW / displayScale;
    const visSrcH = vpH / displayScale;
    const visSrcCX = (displayW / 2 - pan.x) / displayScale;
    const visSrcCY = (displayH / 2 - pan.y) / displayScale;

    let srcW: number, srcH: number;
    if (visSrcW / visSrcH > TARGET_ASPECT) {
      srcW = visSrcW;
      srcH = visSrcW / TARGET_ASPECT;
    } else {
      srcH = visSrcH;
      srcW = visSrcH * TARGET_ASPECT;
    }

    const srcX = Math.max(0, Math.min(naturalW - srcW, visSrcCX - srcW / 2));
    const srcY = Math.max(0, Math.min(naturalH - srcH, visSrcCY - srcH / 2));

    const outW = CROP_OUTPUT_W;
    const outH = Math.round(CROP_OUTPUT_W / TARGET_ASPECT);
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
      onConfirm(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
    };
    img.src = crop.src;
  }, [
    crop.src, naturalW, naturalH, displayW, displayH,
    displayScale, pan, vpW, vpH, TARGET_ASPECT, onConfirm,
  ]);

  const imgStyle: React.CSSProperties = {
    position: "absolute",
    width: displayW,
    height: displayH,
    left: (vpW - displayW) / 2 + pan.x,
    top: (vpH - displayH) / 2 + pan.y,
    userSelect: "none",
    pointerEvents: "none",
  };

  return ReactDOM.createPortal(
    <div className={styles.cropBackdrop}>
      <div className={styles.cropModal}>
        <div className={styles.cropHeader}>
          <span>{t("communityDetail.dragToReposition")}</span>
          <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
            {Math.round(zoom * 100)}%
          </span>
        </div>

        <div
          ref={viewportRef}
          className={styles.cropViewport}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onWheel={onWheel}
          style={{ cursor: dragRef.current ? "grabbing" : "grab" }}
        >
          <img
            src={crop.src}
            alt="Crop preview"
            draggable={false}
            style={imgStyle}
          />
          <div className={styles.cropGrid}>
            {[1, 2].map((i) => (
              <React.Fragment key={i}>
                <div
                  style={{
                    position: "absolute",
                    left: `${(i / 3) * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: "var(--border-default)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: `${(i / 3) * 100}%`,
                    left: 0,
                    right: 0,
                    height: 1,
                    background: "var(--border-default)",
                  }}
                />
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className={styles.cropFooter}>
          <span style={{ fontSize: 11, color: "var(--text-tertiary)", flexShrink: 0 }}>
            {t("communityDetail.zoom")}
          </span>
          <input
            type="range"
            min="1"
            max="4"
            step="0.01"
            value={zoom}
            onChange={handleZoomSlider}
            style={{ flex: 1, accentColor: "var(--pillar-problems, #ef4444)", cursor: "pointer" }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className={styles.cropCancelBtn} onClick={onCancel}>
              {t("workspace.cancel")}
            </button>
            <button type="button" className={styles.cropSaveBtn} onClick={handleConfirm}>
              {t("communityDetail.saveCrop")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ================================================================== */
/*  Menu item icons                                                    */
/* ================================================================== */

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}

/* ================================================================== */
/*  DeleteConfirmDialog — portal-based confirmation before delete      */
/* ================================================================== */

function DeleteConfirmDialog({
  workspaceName,
  onConfirm,
  onCancel,
}: {
  workspaceName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useAppTranslation("pages");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return ReactDOM.createPortal(
    <div className={styles.cropBackdrop} onClick={onCancel}>
      <div className={styles.editModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.editHeader}>
          {t("workspace.deleteConfirmTitle")}
        </div>
        <div className={styles.editBody}>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
            {t("workspace.deleteConfirmMessage", { name: workspaceName })}
          </p>
        </div>
        <div className={styles.editFooter}>
          <button type="button" className={styles.cropCancelBtn} onClick={onCancel}>
            {t("workspace.cancel")}
          </button>
          <button type="button" className={styles.deleteBtn} onClick={onConfirm}>
            {t("workspace.delete")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ================================================================== */
/*  EditWorkspaceModal — inline edit for name + category                */
/* ================================================================== */

const CATEGORY_OPTIONS = [
  "environmental",
  "social",
  "economic",
  "health",
  "infrastructure",
  "governance",
  "education",
  "other",
] as const;

function EditWorkspaceModal({
  workspace,
  onSave,
  onCancel,
}: {
  workspace: WorkspaceResponse;
  onSave: (wsGuid: string, name: string, category: string) => void;
  onCancel: () => void;
}) {
  const { t } = useAppTranslation("pages");
  const [name, setName] = useState(workspace.presentingConcern);
  const [category, setCategory] = useState(workspace.concernCategory || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave(workspace.workspaceGuid, name.trim(), category);
    setSaving(false);
  };

  return ReactDOM.createPortal(
    <div className={styles.cropBackdrop} onClick={onCancel}>
      <div className={styles.editModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.editHeader}>
          {t("workspace.editWorkspace")}
        </div>
        <div className={styles.editBody}>
          <label className={styles.editLabel}>
            {t("workspace.presentingConcern")}
          </label>
          <textarea
            className={styles.editTextarea}
            value={name}
            onChange={(e) => setName(e.target.value)}
            rows={3}
            maxLength={500}
            autoFocus
          />

          <label className={styles.editLabel}>
            {t("workspace.category")}
          </label>
          <select
            className={styles.editSelect}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">{t("workspace.selectCategory")}</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {t(`workspace.categoryLabels.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.editFooter}>
          <button type="button" className={styles.cropCancelBtn} onClick={onCancel}>
            {t("workspace.cancel")}
          </button>
          <button
            type="button"
            className={styles.cropSaveBtn}
            onClick={handleSave}
            disabled={saving || !name.trim()}
          >
            {saving ? "…" : t("workspace.save")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ================================================================== */
/*  WorkspaceList                                                      */
/* ================================================================== */

const WorkspaceList: React.FC<Props> = ({
  communityGuid,
  onOpen,
  onCreate,
  refreshKey,
  maxVisible = 0,
  onViewAll,
  sectionTitle,
  communityName,
  communityFlag,
}) => {
  const { t } = useAppTranslation("pages");
  const [workspaces, setWorkspaces] = useState<WorkspaceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cropState, setCropState] = useState<CropState | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [editingWs, setEditingWs] = useState<WorkspaceResponse | null>(null);
  const [menuOpenGuid, setMenuOpenGuid] = useState<string | null>(null);
  const [deletingWs, setDeletingWs] = useState<WorkspaceResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingWsGuidRef = useRef<string>("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWorkspaces({ communityGuid, isActive: true });
      setWorkspaces(data);
    } catch (e: any) {
      setError(e?.message ?? t("workspace.loadError"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentional: `t` (i18next) has an unstable reference across renders.
    // Including it here regenerates `load` every render, which causes the
    // effect that calls load() to re-fire on every render, producing an
    // infinite fetch loop that exhausts browser sockets and starves other
    // network calls (notably /api/Security/users, which makes sysadmin
    // permission resolution fail). Captured `t` from the closure produces
    // the same localized string regardless of reference identity. DEV-968 fix.
  }, [communityGuid]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const phaseLabel = useCallback(
    (phase: string): string => t(`workspace.phaseLabels.${phase}`) || phase,
    [t]
  );

  const categoryLabel = useCallback(
    (cat: string): string => t(`workspace.categoryLabels.${cat}`) || cat,
    [t]
  );

  /* ── Kebab menu handlers ── */

  const handleMenuToggle = useCallback(
    (e: React.MouseEvent, wsGuid: string) => {
      e.stopPropagation();
      setMenuOpenGuid((prev) => (prev === wsGuid ? null : wsGuid));
    },
    []
  );

  const handleMenuPhoto = useCallback(
    (wsGuid: string) => {
      setMenuOpenGuid(null);
      pendingWsGuidRef.current = wsGuid;
      fileInputRef.current?.click();
    },
    []
  );

  const handleMenuEdit = useCallback(
    (ws: WorkspaceResponse) => {
      setMenuOpenGuid(null);
      setEditingWs(ws);
    },
    []
  );

  const handleMenuDelete = useCallback(
    (ws: WorkspaceResponse) => {
      setMenuOpenGuid(null);
      setDeletingWs(ws);
    },
    []
  );

  /* ── Close menu on outside click ── */

  useEffect(() => {
    if (!menuOpenGuid) return;
    const close = () => setMenuOpenGuid(null);
    // Delay so the toggle click doesn't immediately close
    const id = setTimeout(() => document.addEventListener("click", close), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("click", close);
    };
  }, [menuOpenGuid]);

  /* ── File picker → crop overlay ── */

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";

      if (!file.type.startsWith("image/")) return;
      if (file.size > MAX_FILE_BYTES) return;

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          setCropState({
            src: img.src,
            naturalW: img.naturalWidth,
            naturalH: img.naturalHeight,
            workspaceGuid: pendingWsGuidRef.current,
          });
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    },
    []
  );

  const handleCropConfirm = useCallback(
    async (dataUrl: string) => {
      if (!cropState) return;
      const wsGuid = cropState.workspaceGuid;
      setCropState(null);
      setUploading(wsGuid);
      try {
        const result = await uploadDataUrl(
          dataUrl,
          "workspace-heroes",
          wsGuid,
          "hero.jpg"
        );
        await updateWorkspace(wsGuid, { heroImageUrl: result.key });
        setWorkspaces((prev) =>
          prev.map((ws) =>
            ws.workspaceGuid === wsGuid
              ? { ...ws, heroImageUrl: result.key }
              : ws
          )
        );
      } catch {
        // Silently fail — user can retry
      } finally {
        setUploading(null);
      }
    },
    [cropState]
  );

  const handleCropCancel = useCallback(() => {
    setCropState(null);
  }, []);

  /* ── Edit modal handlers ── */

  const handleEditSave = useCallback(
    async (wsGuid: string, name: string, category: string) => {
      try {
        await updateWorkspace(wsGuid, {
          presentingConcern: name,
          concernCategory: category || undefined,
        });
        setWorkspaces((prev) =>
          prev.map((ws) =>
            ws.workspaceGuid === wsGuid
              ? { ...ws, presentingConcern: name, concernCategory: category }
              : ws
          )
        );
      } catch {
        // Silently fail — user can retry
      }
      setEditingWs(null);
    },
    []
  );

  const handleEditCancel = useCallback(() => {
    setEditingWs(null);
  }, []);

  /* ── Delete confirmation handlers ── */

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingWs) return;
    const wsGuid = deletingWs.workspaceGuid;
    setDeletingWs(null);
    try {
      await deleteWorkspace(wsGuid);
      setWorkspaces((prev) => prev.filter((ws) => ws.workspaceGuid !== wsGuid));
    } catch {
      // Silently fail — user can retry
    }
  }, [deletingWs]);

  const handleDeleteCancel = useCallback(() => {
    setDeletingWs(null);
  }, []);

  /* ── Build location badge from community props ── */
  const locationBadge: LocationBadge | undefined =
    communityName
      ? { flag: communityFlag || "", label: communityName }
      : undefined;

  if (loading) {
    return <div className={styles.loading}>Loading…</div>;
  }

  return (
    <div className={styles.container}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Crop overlay */}
      {cropState && (
        <CardCropOverlay
          crop={cropState}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}

      {/* Edit modal */}
      {editingWs && (
        <EditWorkspaceModal
          workspace={editingWs}
          onSave={handleEditSave}
          onCancel={handleEditCancel}
        />
      )}

      {/* Delete confirmation */}
      {deletingWs && (
        <DeleteConfirmDialog
          workspaceName={deletingWs.presentingConcern}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      <div className={styles.header}>
        <span className={styles.title}>{sectionTitle || t("workspace.title")}</span>
        <button type="button" className={styles.createBtn} onClick={onCreate}>
          + {t("workspace.createWorkspace")}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!error && workspaces.length === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>{t("workspace.noWorkspaces")}</div>
          <div className={styles.emptyHint}>
            {t("workspace.noWorkspacesHint")}
          </div>
        </div>
      )}

      {workspaces.length > 0 && (() => {
        const hasOverflow = maxVisible > 0 && workspaces.length > maxVisible;
        const visible = hasOverflow ? workspaces.slice(0, maxVisible) : workspaces;
        return (
          <>
        <div className={styles.grid}>
          {visible.map((ws) => {
            const isUploading = uploading === ws.workspaceGuid;

            return (
              <WorkspaceCard
                key={ws.workspaceGuid}
                workspace={ws}
                locationBadge={locationBadge}
                onClick={() => onOpen(ws.workspaceGuid)}
              >
                {/* Kebab menu — appears on hover, top-right */}
                <div
                  className={styles.kebabBtn}
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => handleMenuToggle(e, ws.workspaceGuid)}
                  title={t("workspace.moreActions")}
                >
                  {isUploading ? (
                    <span className={styles.cameraSpinner} />
                  ) : (
                    <span className={styles.kebabDots}>&#x22EE;</span>
                  )}
                </div>

                {/* Dropdown menu */}
                {menuOpenGuid === ws.workspaceGuid && (
                  <div className={styles.kebabMenu} onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className={styles.kebabMenuItem}
                      onClick={() => handleMenuEdit(ws)}
                    >
                      <EditIcon /> {t("workspace.editWorkspace")}
                    </button>
                    <button
                      type="button"
                      className={styles.kebabMenuItem}
                      onClick={() => handleMenuPhoto(ws.workspaceGuid)}
                    >
                      <CameraIcon /> {t("workspace.changePhoto")}
                    </button>
                    <div className={styles.kebabDivider} />
                    <button
                      type="button"
                      className={`${styles.kebabMenuItem} ${styles.kebabMenuItemDanger}`}
                      onClick={() => handleMenuDelete(ws)}
                    >
                      <TrashIcon /> {t("workspace.deleteWorkspace")}
                    </button>
                  </div>
                )}
              </WorkspaceCard>
            );
          })}
        </div>
        {hasOverflow && onViewAll && (
          <button type="button" className={styles.viewAllBtn} onClick={onViewAll}>
            {t("communityDetail.viewAllWorkspaces", { count: workspaces.length })}
            {" \u2192"}
          </button>
        )}
          </>
        );
      })()}
    </div>
  );
};

export default WorkspaceList;
